import { db } from '@/main'
import Compressor from 'compressorjs'
import config from '../config'
import axios from 'axios'

export default {
  namespaced: true,
  state: {
    products: [],
    loading: false,
    loadingItem: false,
    uploadProgress: 0,
    error: null,
    filter: {
      branch: '',
      name: '',
      locale: 'en'
    },
    requiredFields: ['branch', 'image', 'name', 'category', 'pricing'],
    optionalFields: ['desc', 'menu'],
    updateFields: [
      'desc',
      'menu',
      'name',
      'category',
      'pricing',
      'branch',
      'image',
      'displayTime',
      'discount'
    ]
  },
  mutations: {
    filterLocale(state, payload) {
      state.filter.locale = payload
    },
    filterBranch(state, payload) {
      state.filter.branch = payload
    },
    filterName(state, payload) {
      state.filter.name = payload
    },
    products(state, payload) {
      state.products = payload || []
    },
    loading(state, payload) {
      state.loading = payload
    },
    loadingItem(state, payload) {
      state.loadingItem = payload
    },
    error(state, payload) {
      state.error = payload
    },
    uploadProgress(state, payload) {
      state.uploadProgress = payload
    },
    startAction(state) {
      state.error = null
      state.loading = true
    },
    startItemAction(state) {
      state.uploadProgress = 0
      state.error = null
      state.loadingItem = true
    },
    stopAction(state) {
      state.loadingItem = false
      state.loading = false
    }
  },
  actions: {
    /**
     * @func
     * @desc query all products of a tenant regardless of branches
     *   this will invoke every time the document in the collection is updated
     *   by using onSnapshot listener
     *   If you create/update/delete a product, this function will also be invoked automatically
     *   so you don't have to update any client side store
     * @param [context]
     */
    subscribeProducts(context) {
      db.collection('tenants')
        .doc(context.rootState.account.tenantId)
        .collection('products')
        .onSnapshot((querySnapshot) => {
          const products = []
          querySnapshot.forEach((snapshot) => {
            const data = snapshot.data()
            data.documentId = snapshot.id
            data.image = `https://${config.firebaseConfig.storageBucket}/${data.image}`
            products.push(data)
          })
          // console.log('new product coming', products)
          context.commit('products', products)
        })
    },
    // activeProduct (context) {
    //   db.collection('tenants')
    //     .doc(context.rootState.account.tenantId)
    //     .collection('products')
    //     .onSnapshot(querySnapshot => {
    //       const products = []
    //       querySnapshot.forEach(snapshot => {
    //         const data = snapshot.data()
    //         data.documentId = snapshot.id
    //         data.image = `https://${config.firebaseConfig.storageBucket}/${data.image}`
    //         products.push(data)
    //       })
    //       console.log('all product', products)
    //     })
    // },

    /**
     * @func
     * @desc create a new product,
     *   After creation, the snapshot collection listener will auto update by itself
     *   This function requires all parameters in @state.requiredFields
     * @param [context]
     * @param {Object} payload
     */
    createProduct(context, payload) {
      context.commit('startItemAction')
      return new Promise((resolve, reject) => {
        // check if all fields are given
        const processed = {}
        const userId = this.getters['account/currentUid']
        processed.active = true
        processed.displayTime = { start: '00:00', end: '23:59' }
        processed.discount = { percentage: payload.discount.percentage }
        let requiredError = false
        context.state.requiredFields.forEach((requiredKey) => {
          if (!payload[requiredKey]) {
            requiredError = true
          }
          processed[requiredKey] = payload[requiredKey]
        })
        // if not given, return error
        if (requiredError) {
          reject(
            new Error(
              `All required fields in ${context.state.requiredFields} are not given`
            )
          )
          return context.commit('stopAction')
        }

        context.state.optionalFields.forEach((optionalKey) => {
          if (optionalKey === 'desc') {
            processed[optionalKey] = payload[optionalKey] || {}
          }
          processed[optionalKey] = payload[optionalKey] || ''
        })

        // const branches = []
        // processed.branch.forEach((branch) => {
        //   branches.push(branch)
        // })
        // processed.branch = branches

        // compress first
        new Compressor(processed.image, {
          // eslint-disable-line no-new
          quality: 0.8,
          error(error) {
            context.commit('error', error)
            reject(error)
            return context.commit('stopAction')
          },
          success(compressedFile) {
            const formData = new FormData()
            formData.append('upload', compressedFile)
            formData.append('type', 'product')
            formData.append('tenantId', context.rootState.account.tenantId)
            axios
              .post('https://api.casstack.com/file', formData, {
                headers: {
                  'Access-Control-Allow-Origin': '*',
                  Api: context.rootState.tenant.api
                },
                params: {
                  tenantId: context.rootState.account.tenantId,
                  userId: userId
                }
              })
              .then((res) => {
                processed.image = res.data.path
                db.collection('tenants')
                  .doc(context.rootState.account.tenantId)
                  .collection('products')
                  .add(processed)
                  .then(() => {
                    return resolve()
                  })
                  .catch((error) => {
                    context.commit('error', error)
                    return reject(error)
                  })
                  .finally(() => {
                    return context.commit('stopAction')
                  })
              })
          }
        })
      })
    },

    /**
     * @func
     * @desc update/edit the product's property,
     *   you have to refer to @state.updateFields to see what are the option available
     *   This function expect a documentId in the payload
     *   e.g. payload = {documentId: xxx, desc: 'new desc', ...}
     * @param [context]
     * @param {Object} payload
     * @return {Promise<unknown>}
     */
    updateProduct(context, payload) {
      context.commit('startItemAction')
      return new Promise((resolve, reject) => {
        if (!payload.documentId) {
          reject(new Error('payload.documentId is required'))
          return context.commit('stopAction')
        }
        const processedPayload = {}
        const userId = this.getters['account/currentUid']
        context.state.updateFields.forEach((allowKey) => {
          if (payload[allowKey] || (allowKey === 'desc' && 'desc' in payload)) {
            // Desc is optional and can be empty
            processedPayload[allowKey] = payload[allowKey]
          }
        })
        if (!processedPayload) {
          resolve({ message: 'No new updates' })
          return context.commit('stopAction')
        }
        // eslint-disable-next-line
        // console.log(processedPayload)
        if (!processedPayload.image) {
          db.collection('tenants')
            .doc(context.rootState.account.tenantId)
            .collection('products')
            .doc(payload.documentId)
            .update(processedPayload)
            .then(() => {
              return resolve()
            })
            .catch((error) => {
              context.commit('error', error)
              return reject(error)
            })
            .finally(() => {
              return context.commit('stopAction')
            })
        } else {
          new Compressor(processedPayload.image, {
            // eslint-disable-line no-new
            quality: 0.8,
            error(error) {
              context.commit('error', error)
              reject(error)
              return context.commit('stopAction')
            },
            success(compressedFile) {
              // console.log(payload.oldImage)
              let imagePath = payload.oldImage.substring(
                payload.oldImage.indexOf('/', 8) + 1
              )
              axios
                .delete('https://api.casstack.com/file/' + imagePath, {
                  headers: {
                    'Access-Control-Allow-Origin': '*',
                    Api: context.rootState.tenant.api
                  },
                  params: {
                    tenantId: context.rootState.account.tenantId,
                    userId: userId
                  }
                })
                .then(() => {
                  // console.log(res)
                  const formData = new FormData()
                  formData.append('upload', compressedFile)
                  formData.append('type', 'product')
                  formData.append(
                    'tenantId',
                    context.rootState.account.tenantId
                  )
                  axios
                    .post('https://api.casstack.com/file', formData, {
                      headers: {
                        'Access-Control-Allow-Origin': '*',
                        Api: context.rootState.tenant.api
                      },
                      params: {
                        tenantId: context.rootState.account.tenantId,
                        userId: userId
                      }
                    })
                    .then((res) => {
                      processedPayload.image = res.data.path
                      db.collection('tenants')
                        .doc(context.rootState.account.tenantId)
                        .collection('products')
                        .doc(payload.documentId)
                        .update(processedPayload)
                        .then(() => {
                          return resolve()
                        })
                        .catch((error) => {
                          context.commit('error', error)
                          return reject(error)
                        })
                        .finally(() => {
                          return context.commit('stopAction')
                        })
                    })
                })
            }
          })
        }
      })
    },

    /**
     * @func
     * @desc deleting a product. After deletion,
     *   the snapshot collection listener will auto update by itself
     *   This function expect a documentId in the payload
     *   e.g. payload = {documentId: xxx}
     * @param [context]
     * @param {Object} payload
     * @return {Promise<unknown>}
     */
    deleteProduct(context, payload) {
      context.commit('startItemAction')
      return new Promise((resolve, reject) => {
        if (!payload.documentId) {
          reject(new Error('payload.documentId is required'))
          return context.commit('stopAction')
        }
        let imagePath = payload.image.substring(
          payload.image.indexOf('/', 8) + 1
        )
        // console.log(context.rootState.account.tenantId)
        // console.log(context.rootState.tenant.api)
        axios
          .delete('https://api.casstack.com/file/' + imagePath, {
            headers: {
              'Access-Control-Allow-Origin': '*',
              Api: context.rootState.tenant.api
            },
            params: {
              tenantId: context.rootState.account.tenantId,
              userId: this.getters['account/currentUid']
            }
          })
          .then(() => {
            db.collection('tenants')
              .doc(context.rootState.account.tenantId)
              .collection('products')
              .doc(payload.documentId)
              .delete()
              .then(() => {
                return resolve()
              })
              .catch((error) => {
                context.commit('error', error)
                return reject(error)
              })
              .finally(() => {
                return context.commit('stopAction')
              })
          })
          .catch((error) => {
            context.commit('error', error)
            return reject(error)
          })
      })
    },
    showProducts(context, payload) {
      return new Promise((resolve, reject) => {
        db.collection('tenants')
          .doc(context.rootState.account.tenantId)
          .collection('products')
          .doc(payload.documentId)
          .update({ active: payload.active })
          .then(() => {
            return resolve()
          })
          .catch((error) => {
            context.commit('error', error)
            return reject(error)
          })
          .finally(() => {
            return context.commit('stopAction')
          })
      })
    }
    // setProductHour (context, payload) {
    //   return new Promise((resolve, reject) => {
    //     const work = payload.newBusHour.map(x => ({
    //       start: x.start,
    //       end: x.end
    //     }))
    //     db.collection('tenants')
    //       .doc(context.rootState.account.tenantId)
    //       .collection('products')
    //       .doc(payload.documentId)
    //       .then(() => {
    //         return resolve()
    //       })
    //       .catch(error => {
    //         return reject(error)
    //       })
    //   })
    // }
  },
  getters: {
    productCount(state) {
      if (!state.products || state.products.length < 1) {
        return 0
      }
      return state.products.length
    },
    productAfterFilter(state) {
      if (!state.products || state.products.length < 1) {
        return []
      }
      return state.products.filter((x) => {
        if (typeof x.active === 'undefined') {
          x.active = true
        }
        if (typeof x.active === 'undefined') {
          x.active = true
        }
        if (!x.discount) {
          x.discount = { percentage: null }
          if (!x.discount.percentage) {
            x.discount.percentage = null
          }
        }
        if (!x.displayTime) {
          x.displayTime = { start: '' }
          x.displayTime = { end: '' }
          if (!x.displayTime.start) {
            x.displayTime.start = '00:00'
            x.displayTime.end = '23:59'
          }
        }

        // console.log(x)
        const takeBranch = state.filter.branch || false
        const takeName = state.filter.name || false

        if (takeBranch && takeName) {
          if (typeof x.name === 'object') {
            return (
              x.branch.includes(state.filter.branch) &&
              x.name[state.filter.locale]
                .toLowerCase()
                .includes(state.filter.name.toLowerCase())
            )
          } else {
            return (
              x.branch.includes(state.filter.branch) &&
              x.name.toLowerCase().includes(state.filter.name.toLowerCase())
            )
          }
        }
        if (takeBranch) {
          return x.branch.includes(state.filter.branch)
        }
        if (takeName) {
          if (typeof x.name === 'object') {
            return x.name[state.filter.locale]
              .toLowerCase()
              .includes(state.filter.name.toLowerCase())
          } else {
            return x.name
              .toLowerCase()
              .includes(state.filter.name.toLowerCase())
          }
        }
        return true
      })
    }
  }
}
